// components/Cancel.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const Cancel = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'rgb(243, 243, 243)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '20px' : '0'
    }}>
      <div style={{
        maxWidth: isMobile ? '100%' : '500px',
        width: isMobile ? '100%' : '90%',
        position: 'relative'
      }}>
        {/* Canvas background effect */}
        <canvas 
          width="250"
          height="60"
          style={{
            position: 'absolute',
            inset: 0,
            height: '100%',
            width: '100%',
            borderRadius: '23px',
            pointerEvents: 'none'
          }}
        />
        
        {/* Gradient overlay */}
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          justifyContent: 'center',
          top: 0,
          overflow: 'hidden',
          borderRadius: '23px',
          pointerEvents: 'none'
        }}>
          <div style={{
            width: '320px',
            height: '320px',
            background: 'linear-gradient(to right, #FF6B6B, #4ECDC4, #45B7D1)',
            aspectRatio: '1',
            opacity: 0.15,
            position: 'absolute',
            top: 0,
            filter: 'blur(32px)',
            borderRadius: '50%',
            transform: 'translateY(-50%)'
          }} />
        </div>

        {/* Main content container with dark theme */}
        <div style={{
          background: 'radial-gradient(ellipse at bottom, #1b2735 0%, #090a0f 100%)',
          borderRadius: isMobile ? '12px' : '23px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          padding: isMobile ? '40px 24px' : '60px 40px',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
          textAlign: 'center'
        }}>
          {/* Star Layer 1: Chaotic zigzag pattern */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(2px 2px at 20px 30px, rgba(255,255,255,0.8), transparent),
              radial-gradient(1px 1px at 80px 60px, #ffffff, transparent),
              radial-gradient(2px 2px at 140px 100px, rgba(255,255,255,0.6), transparent)
            `,
            backgroundRepeat: 'repeat',
            backgroundSize: '200px 150px',
            opacity: 1,
            animation: 'randomMove1 345s ease-in-out infinite, twinkle1 4.2s linear infinite',
            pointerEvents: 'none'
          }} />
          
          {/* Star Layer 2: Swirling motion */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(3px 3px at 60px 90px, #ffffff, transparent),
              radial-gradient(2px 2px at 120px 40px, #ffffff, transparent),
              radial-gradient(1px 1px at 180px 110px, rgba(255,255,255,0.9), transparent)
            `,
            backgroundRepeat: 'repeat',
            backgroundSize: '250px 200px',
            opacity: 1,
            pointerEvents: 'none'
          }} />
          
          {/* Star Layer 3: Figure-8 pattern */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(1px 1px at 40px 70px, rgba(255,255,255,0.7), transparent),
              radial-gradient(2px 2px at 100px 30px, #ffffff, transparent),
              radial-gradient(1px 1px at 160px 90px, rgba(255,255,255,0.5), transparent)
            `,
            backgroundRepeat: 'repeat',
            backgroundSize: '200px 150px',
            opacity: 0.8,
            animation: 'randomMove3 320s linear infinite, twinkle1 3.2s linear infinite',
            pointerEvents: 'none'
          }} />
          
          {/* Star Layer 4: Random bouncing */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(2px 2px at 30px 50px, rgba(255,255,255,0.6), transparent),
              radial-gradient(3px 3px at 90px 120px, #ffffff, transparent),
              radial-gradient(1px 1px at 170px 80px, rgba(255,255,255,0.8), transparent)
            `,
            backgroundRepeat: 'repeat',
            backgroundSize: '220px 170px',
            opacity: 0.9,
            animation: 'randomMove4 240s ease-in-out infinite, twinkle2 4.5s ease-in-out infinite',
            pointerEvents: 'none'
          }} />
          
          {/* Star Layer 5: Spiral motion */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(1px 1px at 50px 20px, #ffffff, transparent),
              radial-gradient(2px 2px at 110px 80px, rgba(255,255,255,0.7), transparent),
              radial-gradient(3px 3px at 190px 140px, #ffffff, transparent)
            `,
            backgroundRepeat: 'repeat',
            backgroundSize: '240px 180px',
            opacity: 0.7,
            animation: 'randomMove5 115s cubic-bezier(0.25, 0.46, 0.45) infinite, twinkleBright 2.5s ease-in-out infinite',
            pointerEvents: 'none'
          }} />
          
          {/* Star Layer 6: Erratic movement */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(2px 2px at 70px 40px, rgba(255,255,255,0.9), transparent),
              radial-gradient(1px 1px at 130px 100px, #ffffff, transparent),
              radial-gradient(2px 2px at 200px 60px, rgba(255,255,255,0.6), transparent)
            `,
            backgroundRepeat: 'repeat',
            backgroundSize: '260px 160px',
            opacity: 0.85,
            animation: 'randomMove6 125s linear infinite, twinkle1 2s linear infinite',
            pointerEvents: 'none'
          }} />
          
          {/* Star Layer 7: Wave-like motion */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(3px 3px at 45px 65px, #ffffff, transparent),
              radial-gradient(2px 2px at 155px 35px, #ffffff, transparent),
              radial-gradient(1px 1px at 95px 115px, rgba(255,255,255,0.8), transparent)
            `,
            backgroundRepeat: 'repeat',
            backgroundSize: '200px 150px',
            opacity: 1,
            pointerEvents: 'none'
          }} />

          {/* Cancel Icon */}
          <div style={{
            width: isMobile ? '60px' : '80px',
            height: isMobile ? '60px' : '80px',
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: `0 auto ${isMobile ? '24px' : '30px'}`,
            boxShadow: '0 0 40px rgba(239, 68, 68, 0.6), 0 4px 14px rgba(239, 68, 68, 0.3)',
            position: 'relative',
            zIndex: 1
          }}>
            <svg 
              width={isMobile ? "30" : "40"} 
              height={isMobile ? "30" : "40"} 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="white" 
              strokeWidth="3" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </div>

          <h1 style={{
            fontSize: isMobile ? '24px' : '32px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            color: '#ffffff',
            marginBottom: isMobile ? '12px' : '16px',
            fontWeight: '600',
            lineHeight: '1.2',
            position: 'relative',
            zIndex: 1
          }}>
            Payment Cancelled
          </h1>

          <p style={{
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            fontSize: isMobile ? '14px' : '16px',
            color: 'rgba(255, 255, 255, 0.7)',
            fontWeight: '400',
            lineHeight: '1.5',
            marginBottom: isMobile ? '30px' : '40px',
            position: 'relative',
            zIndex: 1
          }}>
            Your payment was cancelled. No charges were made to your account.
          </p>

          <button
            onClick={() => navigate('/')}
            style={{
              padding: isMobile ? '10px 20px' : '12px 28px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '6px',
              color: '#ffffff',
              fontSize: isMobile ? '13px' : '14px',
              fontWeight: '500',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              transition: 'all 0.15s ease',
              backdropFilter: 'blur(10px)',
              cursor: 'pointer',
              position: 'relative',
              zIndex: 1
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
              e.target.style.boxShadow = '0 1px 3px 0 rgba(255,255,255,0.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
              e.target.style.boxShadow = 'none';
            }}
          >
            Return Home
          </button>
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          @keyframes twinkle1 {
            0%, 100% { opacity: 0.8; }
            50% { opacity: 0.3; }
          }
          
          @keyframes twinkle2 {
            0%, 100% { opacity: 0.9; }
            30% { opacity: 0.2; }
            60% { opacity: 0.5; }
          }
          
          @keyframes twinkleBright {
            0%, 100% { 
              opacity: 0.2;
              transform: scale(1);
            }
            50% { 
              opacity: 1;
              transform: scale(1.2);
            }
          }
          
          @keyframes randomMove1 {
            0% { transform: translate(0, 0); }
            15% { transform: translate(35px, -45px); }
            30% { transform: translate(-25px, -30px); }
            45% { transform: translate(45px, 20px); }
            60% { transform: translate(-40px, 35px); }
            75% { transform: translate(30px, -25px); }
            90% { transform: translate(-20px, 40px); }
            100% { transform: translate(0, 0); }
          }
          
          @keyframes randomMove2 {
            0% { transform: translate(0, 0) rotate(0deg); }
            12% { transform: translate(-30px, 25px) rotate(45deg); }
            25% { transform: translate(40px, 40px) rotate(90deg); }
            37% { transform: translate(35px, -35px) rotate(135deg); }
            50% { transform: translate(-45px, -20px) rotate(180deg); }
            62% { transform: translate(-35px, 30px) rotate(225deg); }
            75% { transform: translate(25px, -40px) rotate(270deg); }
            87% { transform: translate(20px, 35px) rotate(315deg); }
            100% { transform: translate(0, 0) rotate(360deg); }
          }
          
          @keyframes randomMove3 {
            0%, 100% { transform: translate(0, 0); }
            10% { transform: translate(-20px, -30px); }
            20% { transform: translate(30px, -40px); }
            30% { transform: translate(40px, 10px); }
            40% { transform: translate(-10px, 40px); }
            50% { transform: translate(-40px, 20px); }
            60% { transform: translate(-30px, -35px); }
            70% { transform: translate(35px, -20px); }
            80% { transform: translate(25px, 30px); }
            90% { transform: translate(-35px, 15px); }
          }
          
          @keyframes randomMove4 {
            0% { transform: translate(0, 0) scale(1); }
            8% { transform: translate(25px, 35px) scale(0.9); }
            16% { transform: translate(-40px, 25px) scale(1.1); }
            24% { transform: translate(-30px, -40px) scale(0.95); }
            32% { transform: translate(35px, -25px) scale(1.05); }
            40% { transform: translate(40px, 30px) scale(0.9); }
            48% { transform: translate(-25px, 40px) scale(1.1); }
            56% { transform: translate(-35px, -30px) scale(0.95); }
            64% { transform: translate(30px, -35px) scale(1.05); }
            72% { transform: translate(20px, 25px) scale(0.9); }
            80% { transform: translate(-30px, 20px) scale(1.1); }
            88% { transform: translate(25px, -20px) scale(0.95); }
            100% { transform: translate(0, 0) scale(1); }
          }
          
          @keyframes randomMove5 {
            0%, 100% { transform: translate(0, 0) rotate(0deg); }
            5% { transform: translate(10px, -15px) rotate(36deg); }
            10% { transform: translate(25px, -25px) rotate(72deg); }
            15% { transform: translate(40px, -20px) rotate(108deg); }
            20% { transform: translate(45px, -5px) rotate(144deg); }
            25% { transform: translate(40px, 15px) rotate(180deg); }
            30% { transform: translate(25px, 30px) rotate(216deg); }
            35% { transform: translate(5px, 35px) rotate(252deg); }
            40% { transform: translate(-15px, 30px) rotate(288deg); }
            45% { transform: translate(-30px, 15px) rotate(324deg); }
            50% { transform: translate(-35px, -5px) rotate(360deg); }
            55% { transform: translate(-30px, -25px) rotate(396deg); }
            60% { transform: translate(-15px, -35px) rotate(432deg); }
            65% { transform: translate(5px, -40px) rotate(468deg); }
            70% { transform: translate(25px, -35px) rotate(504deg); }
            75% { transform: translate(40px, -20px) rotate(540deg); }
            80% { transform: translate(45px, 0px) rotate(576deg); }
            85% { transform: translate(35px, 20px) rotate(612deg); }
            90% { transform: translate(20px, 30px) rotate(648deg); }
            95% { transform: translate(5px, 20px) rotate(684deg); }
          }
          
          @keyframes randomMove6 {
            0% { transform: translate(0, 0); }
            5% { transform: translate(-15px, 20px); }
            10% { transform: translate(30px, 15px); }
            15% { transform: translate(25px, -35px); }
            20% { transform: translate(-40px, -25px); }
            25% { transform: translate(-35px, 30px); }
            30% { transform: translate(20px, 40px); }
            35% { transform: translate(40px, -10px); }
            40% { transform: translate(-25px, -40px); }
            45% { transform: translate(-45px, 10px); }
            50% { transform: translate(15px, 35px); }
            55% { transform: translate(35px, -30px); }
            60% { transform: translate(-20px, -35px); }
            65% { transform: translate(-40px, 25px); }
            70% { transform: translate(30px, 30px); }
            75% { transform: translate(45px, -20px); }
            80% { transform: translate(-10px, -30px); }
            85% { transform: translate(-35px, 20px); }
            90% { transform: translate(25px, 25px); }
            95% { transform: translate(20px, -15px); }
            100% { transform: translate(0, 0); }
          }
          
          @keyframes randomMove7 {
            0%, 100% { transform: translate(0, 0) scale(1); }
            6% { transform: translate(15px, -10px) scale(1.05); }
            12% { transform: translate(5px, -25px) scale(0.95); }
            18% { transform: translate(-20px, -30px) scale(1.1); }
            24% { transform: translate(-35px, -15px) scale(0.9); }
            30% { transform: translate(-40px, 10px) scale(1.05); }
            36% { transform: translate(-30px, 30px) scale(0.95); }
            42% { transform: translate(-10px, 40px) scale(1.1); }
            48% { transform: translate(15px, 35px) scale(0.9); }
            54% { transform: translate(35px, 20px) scale(1.05); }
            60% { transform: translate(45px, -5px) scale(0.95); }
            66% { transform: translate(40px, -25px) scale(1.1); }
            72% { transform: translate(25px, -40px) scale(0.9); }
            78% { transform: translate(5px, -45px) scale(1.05); }
            84% { transform: translate(-15px, -35px) scale(0.95); }
            90% { transform: translate(-25px, -15px) scale(1.1); }
            96% { transform: translate(-15px, 5px) scale(0.95); }
          }
        `}
      </style>
    </div>
  );
};

export default Cancel;